import mBaseModel                from "@/models/mBaseModel";
import mReportParameter          from "@/models/mReportParameter";
import service                   from "@/services/HttpRequestService";
import mReportColumn             from "@/models/mReportColumn";
import UtilsData                 from "@/utilities/UtilsData";
import {arrayVueSensitiveConcat} from "@/utilities/ArrayUtils";
import ManagerRequest            from "@/services/ManagerRequest";
import ObjAlertState             from "@/components/common/alert/ObjAlertState";
import ReportColumnsCalculation  from "@/utilities/ReportColumnsCalculation";

export default class mReport extends mBaseModel {
  constructor(rawModelFields = {}, isHistorical = false) {
    super(rawModelFields);
    this.isHistorical = isHistorical;
    this.ats.Id     = 0; // For New Report
    this.ats.Name   = null;
    this.ats.Query  = null;
    this.ats.Source = 0;
    /** @type [mReportColumn] */
    this.ats.Columns = [];
    /** @type [mReportParameter] */
    this.ats.Parameters = [];
    this._localReportData                = null;
    this._localReportDataAsArrayOfArrays = null;
    this.setRawFields(rawModelFields);
    this.setAsInitialState(rawModelFields);
  }

  setRawFields(rawModelFields = {}) {
    for (let prop of Object.keys(rawModelFields)) {
      if (this.ats.hasOwnProperty(prop)) {
        switch (prop) {
          case 'Parameters':
            this.ats[prop] = [];
            for (let [i, obj] of Object.entries(rawModelFields[prop])) {
              this.ats[prop].push(new mReportParameter(obj));
            }
            break;
          case 'Columns':
            this.ats[prop] = [];
            for (let [i, obj] of Object.entries(rawModelFields[prop])) {
              this.ats[prop].push(new mReportColumn(obj));
            }
            break;
          default:
            this.ats[prop] = rawModelFields[prop];
        }
      }
    }
    return this;
  }

  hasParameterDbName(dbName) {
    for (const [i, el] of Object.entries(this.ats.Parameters)) {
      if (el.ats.DbName === dbName) {
        return i;
      }
    }
    return null;
  }

  hasColumnDbName(dbName) {
    for (const [i, el] of Object.entries(this.ats.Columns)) {
      if (el.ats.DbName === dbName) {
        return i;
      }
    }
    return null;
  }

  mapUdfParameters(udfParameters) {
    for (const [i, coreParameter] of Object.entries(this.ats.Parameters)) {
      for (const [i, udfParameter] of Object.entries(udfParameters)) {
        if (coreParameter.ats.DbName === udfParameter.DbName) {
          coreParameter.ats.Value = udfParameter.Value;
        }
      }
    }
    return this;
  }

  getColumnFormatters() {
    let res         = [];
    const rawFields = this.getRawFields();
    const columns   = rawFields.Columns;
    if (columns.length > 0) {
      res = columns;
    }
    return res;
  }

  loopReportData() {
    // region Reset
    this._localReportDataAsArrayOfArrays  = [];
    const _localReportDataAsArrayOfArrays = this._localReportDataAsArrayOfArrays;
    const _localReportData                = this._localReportData;
    const rawReport                       = this.getRawFields();
    const columnFormatters                = this.getColumnFormatters();
    // endregion Reset
    // ##############################
    // region Headers
    // ToDo: Check if the 1st row has ALL possible columns.
    let arrHeaders = Object.keys(_localReportData[0]);
    _localReportDataAsArrayOfArrays.push(arrHeaders.map(el => ReportColumnsCalculation.formatColName(el, columnFormatters)));
    // #####
    if (rawReport.Parameters && rawReport.Parameters.length > 0) {
      const arrParameters = rawReport.Parameters;
      for (let p of arrParameters) {
        let pName  = p.Name;
        let pValue = p.Value;
        _localReportDataAsArrayOfArrays.push([pName, pValue]);
      }
    }
    // #####
    // endregion Headers
    // ##############################
    // region Footer
    let footer = {};
    for (let dbName of arrHeaders) {
      footer[dbName] = null;
    }
    // endregion Footer
    // ##############################
    // region Report Body
    for (let rowReportData of _localReportData) {
      let row = [];
      for (let dbName in rowReportData) {
        if (rowReportData.hasOwnProperty(dbName)) {
          let value = rowReportData[dbName];
          let v     = ReportColumnsCalculation.formatColValue(value, dbName, columnFormatters);
          row.push(v)
          // #####
          if (ReportColumnsCalculation.isFieldInTotals(dbName, columnFormatters)) {
            footer[dbName] = footer[dbName] + parseFloat(value);
          }
          // #####
        }
      }
      _localReportDataAsArrayOfArrays.push(row);
    }
    // #####
    let rowFooter = [];
    for (let [dbName, total] of Object.entries(footer)) {
      let t = ReportColumnsCalculation.formatColValue(total, dbName, columnFormatters);
      rowFooter.push(t);
    }
    _localReportDataAsArrayOfArrays.push(rowFooter);
    _localReportData.push(footer);
    // endregion Report Body
    // ##############################
  }

  // ##################################################
  // region Back-End CRUD
  async run(parameters = null) {
    const rawFields = this.getRawFields();
    const id        = rawFields.Id;
    parameters      = parameters || rawFields.Parameters;
    if (!this.validateParameterValue(parameters)) {
      return false;
    }
    const requestDecorator = new ManagerRequest(
      this.isHistorical ? service.runHistoricalReport : service.runReport,
      service,
      null);
    this._localReportData = await requestDecorator.do(id, parameters);
    return this._localReportData;
  }

  async get() {
    this._localForceUnfreezeWorkflow = false;
    let rawFields                    = {};
    if (!this.isNew()) {
      rawFields = await (new ManagerRequest(
        this.isHistorical ? service.getHistoricalReportById : service.getReportById,
        service,
        rawFields
      ))
      .do(this.ats.Id);
    }
    this.setRawFields(rawFields);
    this.setAsInitialState(rawFields);
    return true;
  }

  async save() {
    const isNew = this.isNew();
    if (isNew) {
      this._localForceUnfreezeWorkflow = true;
    }
    let data      = this.getRawFields();
    let rawFields = {};
    rawFields     = await (new ManagerRequest(
      service.saveReport,
      service,
      rawFields
    ))
    .do(this.ats.Id, data);
    if (!UtilsData.isEmpty(rawFields)) {
      this.setRawFields(rawFields);
      this.setAsInitialState(rawFields);
      if (isNew) {
        this._localForceUnfreezeWorkflow = false;
      }
      return this;
    }
    return false;
  }

  async delete() {
    let res = true;
    res     = await (new ManagerRequest(
      service.deleteReport,
      service,
      res
    ))
    .do(this.ats.Id);
    return res;
  }

  // endregion Back-End  CRUD
  // ##################################################
  // region Data Sources
  static vocSourceToName() {
    const voc = [];
    voc[0]    = 'MySQL';
    voc[1]    = 'RedShift';
    return voc;
  }

  // endregion Data Sources
  // ##################################################
  validate() {
    this._localValidationStatus  = true;
    this._localValidationResults = [];
    let subModelValidateRes      = [];
    // #####
    if (UtilsData.isEmpty(this.ats.Name)) this._localValidationResults.push({field: 'Name', msg: 'Field is required', modelName: 'Report', id: this.Id});
    if (UtilsData.isEmpty(this.ats.Query)) this._localValidationResults.push({field: 'Query', msg: 'Field is required', modelName: 'Report', id: this.Id});
    if (UtilsData.isNull(this.ats.Source)) this._localValidationResults.push({field: 'Source', msg: 'Field is required', modelName: 'Report', id: this.Id});
    // #####
    for (let model of this.ats.Parameters) {
      subModelValidateRes         = model.validate();
      this._localValidationStatus = model._localValidationStatus;
      arrayVueSensitiveConcat(this._localValidationResults, subModelValidateRes);
    }
    for (let model of this.ats.Columns) {
      subModelValidateRes         = model.validate();
      this._localValidationStatus = model._localValidationStatus;
      arrayVueSensitiveConcat(this._localValidationResults, subModelValidateRes);
    }
    // #####
    this._localValidationStatus = this._localValidationResults.length === 0;
    return this._localValidationResults;
  }

  validateParameterValue(rawParameters) {
    let res = true;
    for (const el of rawParameters) {
      //#####
      //region FIX for Date fields
      if (el.Type === 'date') {
        if (el.Value) {
          if (el.Value instanceof Date) {
            el.Value = el.Value.toString();
          }
        }
      }
      //endregion FIX for Date fields
      //#####
      if (UtilsData.isEmpty(el.Value)) {
        const msg = `Parameter ${el.Name} is empty`;
        ObjAlertState.set(msg, 3);
        res = false;
      }
    }
    return res;
  }

  // ##################################################
}
